/**
 * THE TOPIC KEY — one spelling, both directions.
 *
 * A topic is identified by `(channel, key)` on the wire. `info` is keyed by the mint, `candles` by
 * `<mint>:<interval>`, `trades` by `<mint>:<mode>`, and `top` by its NORMALISED SPEC. If the client composed
 * those strings at each call site, one screen would subscribe to `mint:1s` and another to `mint:1S`, and the
 * second would silently receive nothing — the server would happily register a topic that never ticks.
 *
 * `top`'s topic is the normalised spec in CANONICAL KEY ORDER, so two semantically identical subscriptions
 * are one topic and share one serialization. The order is fixed here, not by `JSON.stringify` of an object
 * literal, because object key order is source order and two call sites writing the same three fields in a
 * different order would produce two topics for one board.
 */

import type { Interval, LaunchSpec, TopSpec, TradeMode } from "@kinetiq/wire";

/** The board's limit is normalised SERVER-SIDE to this set. The client clamps to the same set so the topic it
 *  computes matches the topic the server registers — otherwise the ack names a topic the client is not
 *  listening on, and the stitch waits forever on a subscription that exists.
 *
 *  ⚠️ IT IS `LIMIT_LADDER` IN `apps/api/src/ws/topics.ts`, and `tests/top.test.ts` reads that file and
 *  asserts the two are identical. A copy is unavoidable — the browser cannot import the server — so it is
 *  CHECKED rather than trusted. */
export const TOP_LIMITS = [10, 25, 50, 100, 250] as const;

/**
 * UP TO THE NEXT RUNG. NEVER to the nearest, and the direction is the whole decision.
 *
 * A client that asked for 47 rows and is sent 50 renders 47 and ignores three, which costs three base58
 * strings. A client that asked for 47 and is sent 25 renders a SHORT BOARD and has no way to know it — the
 * frame is well-formed and the list simply ends. Over-serving is a rounding error; under-serving is a wrong
 * answer.
 *
 * ⚠️ THIS ROUNDED TO THE NEAREST RUNG UNTIL 2026-08-12, while the server (`quantiseLimit`) has always rounded
 * up. `?limit=30` therefore computed the topic `…:25` on the client and `…:50` on the server: the ack named a
 * topic the client was not listening on, and the board never left `degraded`. `tests/top.test.ts` now pins the
 * direction as well as the ladder.
 */
export function normaliseTopLimit(limit: number): number {
  for (const rung of TOP_LIMITS) if (limit <= rung) return rung;
  return TOP_LIMITS[TOP_LIMITS.length - 1] as number;
}

/** THE FILTER IS PART OF THE KEY. Two boards that differ only by it are DIFFERENT RANKINGS — the filtered
 *  one contains rows the other never had, because the exclusion happens at admission — so they are two
 *  topics. Collapsing them would serialize one board and publish it to the other's subscribers. */
const filterKey = (f: { verified: boolean | null; flagged: boolean | null }): string =>
  `${f.verified === null ? "-" : f.verified ? "v" : "u"}${f.flagged === null ? "-" : f.flagged ? "f" : "c"}`;

export function normaliseTopSpec(spec: TopSpec): TopSpec {
  return {
    sort: spec.sort,
    window: spec.window,
    limit: normaliseTopLimit(spec.limit),
    // `null` IS THE DEFAULT AND IS NOT THE SAME AS `false`. A client that omits the field means "no
    // constraint"; `false` means "only rows that are NOT this". Normalising the two together would silently
    // turn an unfiltered board into a filtered one.
    filter: {
      verified: spec.filter?.verified ?? null,
      flagged: spec.filter?.flagged ?? null,
    },
  };
}

export const infoTopic = (mint: string): string => mint;
export const candlesTopic = (mint: string, interval: Interval): string => `${mint}:${interval}`;
export const tradesTopic = (mint: string, mode: TradeMode): string => `${mint}:${mode}`;

/** The `top` topic. NOT sent as a `key` on the wire — `top` subscribes with a `spec` — but the client needs a
 *  stable string to index its own per-topic state by, and it must be the same string for two equal specs. */
export const topTopic = (spec: TopSpec): string => {
  const n = normaliseTopSpec(spec);
  // ⚠️ `|` AND NOT `:`, BECAUSE THIS STRING IS MATCHED AGAINST THE SERVER'S. The `subscribed` ack echoes
  // `data.topics: ["top:<key>"]` using the server's own `specKey` (`apps/api/src/ws/topics.ts`), and the
  // socket resolves a keyless `top` frame by looking that key up. Two spellings of one key is a frame that
  // routes to nothing — which is exactly how a board switch lost its ack and never issued its seed.
  return `${n.sort}|${n.window}|${n.limit}|${filterKey(n.filter)}`;
};

/**
 * THE LAUNCH FEED'S SPEC, THROUGH THE SAME LADDER — because the server puts it through the same one.
 *
 * `apps/api/src/ws/topics.ts`'s `normaliseLaunchSpec` calls `quantiseLimit`, the SAME function `top` uses,
 * and its own comment says why: the rungs are a serialization-SHARING decision, not a property of what is
 * being listed. So this reuses `normaliseTopLimit` rather than declaring a second ladder — a launch board
 * quantised on different rungs from the server's would compute a topic the ack never names, which is the
 * failure `normaliseTopLimit`'s own comment records for `?limit=30`.
 *
 * THE TOP RUNG (250) IS ALSO `LAUNCH_PAGE_MAX`, so quantising UP can never round a full-page request DOWN
 * into a short list. `tests/launches.test.ts` pins that relation against the server's own file, exactly as
 * `tests/top.test.ts` pins the ladder.
 */
export function normaliseLaunchSpec(spec: LaunchSpec): LaunchSpec {
  return { window: spec.window, limit: normaliseTopLimit(spec.limit) };
}

/** The `launches` topic. Like `top`'s it is NOT sent as a `key` on the wire — a spec channel subscribes with a
 *  `spec` — but the client needs a stable string to index its own per-topic state by, and it is the SERVER's
 *  own `launchSpecKey` spelling (`<window>|<limit>`) so the two can be compared rather than trusted. */
export const launchesTopic = (spec: LaunchSpec): string => {
  const n = normaliseLaunchSpec(spec);
  return `${n.window}|${n.limit}`;
};

/** The per-connection identity of a subscription, used as the key of every per-topic map in the socket. */
export const topicId = (channel: string, key: string): string => `${channel}\0${key}`;
